import { Box, BoxProps } from '@chakra-ui/react';
import { throttle } from 'es-toolkit/function';
import { useEffect, useRef, useState } from 'react';

import styles from './CustomScroll.module.css';
import { useDraggableScroll } from './useDraggableScroll';

type CustomScrollProps = {
  children: React.ReactNode;
  withShadows?: boolean;
  shadowBgColor?: string;
  /** Opt into keyboard scrolling without adding tab stops to every scroll surface. */
  contentTabIndex?: number;
  contentLabel?: string;
} & BoxProps;

const shadowStyle = {
  backgroundSize: '10px 100%, 100% 100%',
  backgroundRepeat: 'no-repeat',
  height: '100%',
  width: '56px',
  content: '""',
  display: 'block',
  transition: 'opacity var(--chakra-durations-fast) linear',
  position: 'absolute',
  top: 0,
  zIndex: 'overlay',
  pointerEvents: 'none',
};

export const CustomScroll = ({
  children,
  withShadows = false,
  shadowBgColor = 'var(--chakra-colors-bg-surface)',
  contentTabIndex,
  contentLabel,
  ...props
}: CustomScrollProps) => {
  const contentRef = useRef<HTMLElement | null>(null);
  const [isNearEnd, setIsNearEnd] = useState(true);
  const [isNearStart, setIsNearStart] = useState(true);

  useEffect(() => {
    const scrolledContent = contentRef.current;

    if (!scrolledContent || !withShadows) {
      return;
    }

    const updateShadows = () => {
      const { offsetWidth, scrollLeft, scrollWidth } = scrolledContent;
      const newIsNearStart = scrollLeft <= 10;
      const newIsNearEnd = scrollWidth - (offsetWidth + scrollLeft) <= 10;
      setIsNearEnd(newIsNearEnd);
      setIsNearStart(newIsNearStart);
    };
    const onScroll = throttle(updateShadows, 50);
    updateShadows();
    const observer = new window.ResizeObserver(updateShadows);
    observer.observe(scrolledContent);
    if (scrolledContent.firstElementChild) observer.observe(scrolledContent.firstElementChild);

    scrolledContent.addEventListener('scroll', onScroll);

    return () => {
      scrolledContent.removeEventListener('scroll', onScroll);
      onScroll.cancel();
      observer.disconnect();
    };
  }, [withShadows]);

  const { onMouseDown } = useDraggableScroll(contentRef);

  return (
    <Box
      {...props}
      pos="relative"
      onMouseDown={onMouseDown}
      {...(withShadows && {
        zIndex: 'docked',
        _before: {
          background: `
            linear-gradient(90deg,
              ${shadowBgColor} 50%,
              ${shadowBgColor} 50%),
            linear-gradient(to right,
              ${shadowBgColor} 20%,
              hsla(0,0%,100%,0) 80%)`,
          opacity: isNearStart ? 0 : 1,
          left: -1,
          ...shadowStyle,
        },
        _after: {
          background: `
            linear-gradient(90deg, 
              ${shadowBgColor} 0%, 
              ${shadowBgColor} 100%),
            linear-gradient(to left,
              ${shadowBgColor} 20%,
              hsla(0,0%,100%,0) 80%)`,
          backgroundPosition: 'right 0, 0 0',
          opacity: isNearEnd ? 0 : 1,
          right: -1,
          ...shadowStyle,
        },
      })}
      overflow="hidden"
    >
      <Box
        ref={contentRef}
        overflowX="scroll"
        className={styles.wrapper}
        tabIndex={contentTabIndex}
        aria-label={contentLabel}
        onKeyDown={
          contentTabIndex === undefined
            ? undefined
            : (event) => {
                if (
                  event.target !== event.currentTarget ||
                  event.altKey ||
                  event.ctrlKey ||
                  event.metaKey ||
                  event.shiftKey ||
                  (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
                )
                  return;
                event.preventDefault();
                event.currentTarget.scrollBy({
                  left:
                    ((event.key === 'ArrowRight' ? 1 : -1) * event.currentTarget.clientWidth) / 2,
                });
              }
        }
        focusVisibleRing={contentTabIndex === undefined ? undefined : 'inside'}
        focusRingColor="purple.focusRing"
      >
        {/* <SimpleBar timeout={400} scrollableNodeProps={{ }}> */}
        {children}
      </Box>
      {/* </SimpleBar> */}
    </Box>
  );
};
